import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaLayerGroup } from '@react-icons/all-files/fa/FaLayerGroup';
import ProfaseModal from '../../commons/ProfaseModal';
import PageForm, { FormField } from '../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../store';
import { getEntity, updateEntity, reset } from '../../manager/workflow-clase.reducer';

const ACCENT = '#6366f1';

interface ClaseForm {
  id: string;
  nombre: string;
}

const empty: ClaseForm = { id: '', nombre: '' };

const fields: FormField<ClaseForm>[] = [
  {
    name:     'id',
    label:    'ID',
    readOnly: true,
    editOnly: true,
  },
  {
    name:        'nombre',
    label:       'Nombre',
    required:    true,
    placeholder: 'Nombre de la clase',
  },
];

export default function WorkflowClaseEditModal() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity        = useAppSelector(s => s.workflowClase.entity) as any;
  const loading       = useAppSelector(s => s.workflowClase.loading);
  const updating      = useAppSelector(s => s.workflowClase.updating);
  const updateSuccess = useAppSelector(s => s.workflowClase.updateSuccess);

  const [form, setForm]     = useState<ClaseForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof ClaseForm, string>>>({});

  useEffect(() => {
    if (id) dispatch(getEntity(id));
    return () => { dispatch(reset()); };
  }, [id, dispatch]);

  useEffect(() => {
    if (entity?.id && String(entity.id) === String(id)) {
      setForm({
        id:     String(entity.id),
        nombre: entity.nombre ?? '',
      });
    }
  }, [entity, id]);

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate('/workflow/clases');
    }
  }, [updateSuccess, dispatch, navigate]);

  const close = () => navigate('/workflow/clases');

  const handleFieldChange = (name: keyof ClaseForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Partial<Record<keyof ClaseForm, string>> = {};
    if (!form.nombre.trim()) nextErrors.nombre = 'El nombre es requerido';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    dispatch(updateEntity({ id: Number(form.id), nombre: form.nombre.trim() }));
  };

  return (
    <ProfaseModal
      open
      titleId="workflow-clase-edit-modal-title"
      size="compact"
    >
      <div style={s.header}>
        <h2 id="workflow-clase-edit-modal-title" className="profase-modal-title m-0">
          <FaLayerGroup style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Editar Clase
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={close}
          disabled={updating}
        >
          Cerrar
        </button>
      </div>

      <p className="profase-modal-description">
        Modifique los datos de la clase de workflow.
      </p>

      <PageForm
        embedded
        fields={fields}
        values={form}
        errors={errors}
        isNew={false}
        saving={updating || loading}
        labelInline
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={close}
        accentColor={ACCENT}
        submitLabel="Guardar cambios"
        cancelLabel="Cancelar"
      />
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
};
